import { DeleteIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteWorkItem } from '@/hooks'
import type { WorkItem } from '@/types'
import { X } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteWorkItemDialogProps {
  projectId: string
  workItem: WorkItem
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteWorkItemDialog({ projectId, workItem, open, onOpenChange }: DeleteWorkItemDialogProps) {
  const deleteWorkItem = useDeleteWorkItem(projectId)

  const handleDelete = async () => {
    try {
      await deleteWorkItem.mutateAsync(workItem.id)
      toast.success('Work item deleted successfully')
      onOpenChange(false)
    } catch {
      toast.error('Failed to delete work item')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete Work Item?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the work item "<span className="font-bold">{workItem.title}</span>"? This
          action cannot be undone and the work item will be permanently removed from the list.
        </DialogDescription>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteWorkItem.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteWorkItem.isPending ? 'Deleting...' : 'Delete Work Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
